import {
  DeesElement,
  customElement,
  html,
  css,
  cssManager,
  property,
  type TemplateResult,
} from '@design.estate/dees-element';
import { dashboardCardStyles } from './sz-dashboard-card.styles.js';

declare global {
  interface HTMLElementTagNameMap {
    'sz-platform-services-card': SzPlatformServicesCard;
  }
}

export interface IPlatformService {
  name: string;
  status: string;
  running: boolean;
  url?: string;
}

@customElement('sz-platform-services-card')
export class SzPlatformServicesCard extends DeesElement {
  public static demo = () => html`
    <div style="padding: 24px; max-width: 500px;">
      <sz-platform-services-card
        .services=${[
          { name: 'MongoDB', status: '1 DB', running: true },
          { name: 'S3 Storage (MinIO)', status: '1 bucket', running: true },
          { name: 'SmartProxy Reverse Proxy', status: 'Running', running: true },
          { name: 'ClickHouse', status: '1 DB', running: true },
        ]}
      ></sz-platform-services-card>
    </div>
  `;

  public static demoGroups = ['Platform'];

  @property({ type: Array })
  public accessor services: IPlatformService[] = [];

  public static styles = [
    cssManager.defaultStyles,
    dashboardCardStyles,
    css`
      .card-content {
        padding: var(--dees-spacing-sm);
      }

      .services-list {
        display: flex;
        flex-direction: column;
      }

      .service-item {
        width: 100%;
        min-height: 48px;
        padding: var(--dees-spacing-sm) var(--dees-spacing-md);
        border: 0;
        border-radius: var(--dees-radius-lg);
        corner-shape: var(--dees-corner-shape);
        background: transparent;
        color: inherit;
        font: inherit;
        text-align: left;
        cursor: pointer;
        display: grid;
        grid-template-columns: auto minmax(0, 1fr) auto auto;
        align-items: center;
        gap: var(--dees-spacing-sm);
        transition:
          background var(--dees-transition-fast) var(--dees-ease-standard),
          transform var(--dees-transition-fast) var(--dees-ease-snappy);
      }

      .service-item:hover {
        background: var(--dees-color-hover);
      }

      .service-item:active {
        transform: scale(0.99);
      }

      .service-item:focus-visible {
        outline: 2px solid var(--dees-color-focus-ring);
        outline-offset: -2px;
      }

      .service-leading-icon,
      .service-chevron {
        color: var(--dees-color-text-muted);
      }

      .service-name {
        overflow: hidden;
        color: var(--dees-color-text-primary);
        font-size: var(--dees-font-callout-size);
        font-weight: var(--dees-font-control-weight);
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      @media (max-width: 460px) {
        .service-item {
          grid-template-columns: auto minmax(0, 1fr) auto;
        }

        .service-chevron {
          display: none;
        }
      }
    `,
  ];

  public render(): TemplateResult {
    return html`
      <dees-tile>
        <div slot="header" class="card-header">
          <div class="header-copy">
            <div class="card-title">Platform Services</div>
            <div class="card-subtitle">Infrastructure status</div>
          </div>
          <dees-icon icon="lucide:ServerCog" .iconSize=${18}></dees-icon>
        </div>

        <div class="card-content">
          ${this.services.length > 0
            ? html`
                <div class="services-list">
                  ${this.services.map(
                    (service) => html`
                      <button
                        class="service-item"
                        type="button"
                        aria-label=${`Open ${service.name}, ${service.status}`}
                        @click=${() => this.handleServiceClick(service)}
                      >
                        <dees-icon
                          class="service-leading-icon"
                          icon="lucide:Server"
                          .iconSize=${16}
                        ></dees-icon>
                        <span class="service-name">${service.name}</span>
                        <dees-badge
                          rounded
                          .type=${this.getStatusType(service)}
                          .text=${service.status}
                        ></dees-badge>
                        <dees-icon
                          class="service-chevron"
                          icon="lucide:ChevronRight"
                          .iconSize=${16}
                        ></dees-icon>
                      </button>
                    `
                  )}
                </div>
              `
            : html`<div class="empty-state">No platform services available.</div>`}
        </div>
      </dees-tile>
    `;
  }

  private getStatusType(
    serviceArg: IPlatformService,
  ): 'default' | 'success' | 'warning' | 'error' {
    if (serviceArg.running) return 'success';
    const normalizedStatus = serviceArg.status.toLowerCase();
    if (normalizedStatus.includes('starting') || normalizedStatus.includes('stopping')) {
      return 'warning';
    }
    if (normalizedStatus.includes('failed')) return 'error';
    return 'default';
  }

  private handleServiceClick(service: IPlatformService) {
    this.dispatchEvent(
      new CustomEvent('service-click', {
        detail: service,
        bubbles: true,
        composed: true,
      })
    );
  }
}
